import { LabCellPatch } from 'modules/pond/api/types'

import { ShrimpHealthThreshold } from 'modules/notifications/api/types'

import {
  DISEASE_GROUPS,
  FIELD_MAPPING,
  LABELS,
  SHRIMP_HEALTH_COLOR_BY_LABEL,
  SHRIMP_HEALTH_TARGET_BY_SUB_GROUP,
  SUB_GROUPS,
  THRESHOLD_VALUES,
} from './constants'

export const isDiseaseGroup = (group: string): boolean => {
  return DISEASE_GROUPS.includes(group as any)
}

export const isAgeRow = (label: string): boolean => {
  return label.toLowerCase().includes(LABELS.AGE.toLowerCase())
}

export const isDisabledColumn = (value: string | number): boolean => {
  const numValue = typeof value === 'number' ? value : parseFloat(value as string)
  return (
    numValue === THRESHOLD_VALUES.DEFAULT_THRESHOLD || value === THRESHOLD_VALUES.DEFAULT_THRESHOLD
  )
}

const getFieldMapping = (subGroupLower: string, labelLower: string) => {
  const subGroupKeys = Object.keys(FIELD_MAPPING)
  const subGroupKey = subGroupKeys.find((key) => subGroupLower.includes(key.toLowerCase()))

  if (!subGroupKey) return null

  const mapping = FIELD_MAPPING[subGroupKey as keyof typeof FIELD_MAPPING]
  const labelKeys = Object.keys(mapping)
  const labelKey = labelKeys.find((k) => k.toLowerCase() === labelLower)

  return labelKey ? mapping[labelKey as keyof typeof mapping] : null
}

/**
 * NK-305: the PATCH body for one edited cell — `{ id, <field>: value }`, where an
 * empty cell (`-` or '') clears the field with `null`. Only that field is sent:
 * the endpoints keep every field left out, so a stale copy of the other cells
 * can no longer overwrite them. `null` when the row maps to no field.
 */
export const buildLabCellPatch = (
  id: number,
  subGroup: string | undefined,
  label: string,
  value: string
): LabCellPatch | null => {
  const fieldName = getFieldMapping(subGroup?.toLowerCase() || '', label.toLowerCase())
  if (!fieldName) return null

  const trimmed = value.trim()
  return { id, [fieldName]: trimmed === '' || trimmed === '-' ? null : Number(trimmed) }
}

export const generateCellKey = (
  group: string,
  subGroup: string | undefined,
  label: string,
  id: number
): string => {
  return `${group}_${subGroup || 'no-subgroup'}_${label}_${id}`
}

interface ColorThresholdRow {
  group: string
  subGroup?: string
  label: string
  values: (number | string)[]
}

/**
 * NK-273: per-column colour limit of each lab-table row, from the
 * organization's shrimp-health alert settings (ตั้งค่าแจ้งเตือนสุขภาพกุ้ง).
 *
 * For a pond row, column i gets the `max` of the band covering that pond's
 * shrimp age on that date (the group's อายุกุ้ง row), or `null` when no band
 * covers it — the cell then stays uncoloured, like the alert. Green is judged
 * on that single day (no consecutive-days rule here). The users' on/off
 * switches do not apply.
 *
 * Returns `undefined` for a row without a limit to look up — age rows and the
 * treatment-pond groups (no shrimp age; they keep the fixed rule) — and for
 * every row while the settings are not loaded.
 */
export const buildColorThresholds = (
  rows: ColorThresholdRow[],
  thresholds: ShrimpHealthThreshold[] | undefined
): ((number | null)[] | undefined)[] => {
  if (!thresholds) return rows.map(() => undefined)

  const agesByGroup = new Map<string, number[]>()
  rows.forEach((row) => {
    if (isAgeRow(row.label)) agesByGroup.set(row.group, row.values.map((v) => Number(v)))
  })

  return rows.map((row) => {
    if (isAgeRow(row.label) || isDiseaseGroup(row.group)) return undefined

    const target = row.subGroup ? SHRIMP_HEALTH_TARGET_BY_SUB_GROUP[row.subGroup] : undefined
    const color = SHRIMP_HEALTH_COLOR_BY_LABEL[row.label]
    const ages = agesByGroup.get(row.group)
    const setting = thresholds.find((t) => t.target === target && t.color === color)
    if (!setting || !ages) return undefined

    return row.values.map((_, i) => {
      const age = ages[i]
      const band = setting.age_ranges.find((r) => age >= r.age_from && age <= r.age_to)
      return band ? band.max : null
    })
  })
}
